import { Button, Group, Text, Tooltip } from "@mantine/core";
import {
  IconCheck,
  IconEye,
  IconX,
} from "@tabler/icons-react";
import { CollaborationStatus } from "@/components/shared/constants/general.const";
import { useDisclosure } from "@mantine/hooks";
import AcceptInterestModal from "@/pages/interests/components/AcceptInterestModal";
import PaymentWindowExtendModal from "@/components/shared/PaymentWindowExtendModal";
import { usePaymentWindowUiDemo } from "@/shared/utils/paymentWindowUiDemo";

export interface InterestRequestActionsProps {
  matchId: string;
  companyId: string;
  collaborationStatus: string;
  isRequester: boolean;
  requesterPaid: boolean;
  receiverPaid: boolean;
  requesterSignedNDA: boolean;
  receiverSignedNDA: boolean;
  paymentDeadline?: Date | string | null;
  onAccept?: (matchId: string) => void;
  onDecline?: (matchId: string) => void;
  onMakePayment?: (matchId: string) => void;
  onViewProfile?: (companyId: string) => void;
  onOpenWorkspace?: (matchId: string) => void;
  isLoading?: boolean;
  /** Table rows use a tighter vertical stack; cards use horizontal groups. */
  variant?: "card" | "table";
}

export default function InterestRequestActions({
  matchId,
  companyId,
  collaborationStatus,
  isRequester,
  requesterPaid,
  receiverPaid,
  requesterSignedNDA,
  receiverSignedNDA,
  paymentDeadline,
  onAccept,
  onDecline,
  onMakePayment,
  onViewProfile,
  onOpenWorkspace,
  isLoading = false,
  variant = "card",
}: InterestRequestActionsProps) {
  const [acceptModalOpened, { open: openAcceptModal, close: closeAcceptModal }] = useDisclosure(false);

  const userPaid = isRequester ? requesterPaid : receiverPaid;

  const showDeadline =
    !!paymentDeadline &&
    (collaborationStatus === CollaborationStatus.BOTH_ACCEPTED ||
      collaborationStatus === CollaborationStatus.REQUESTER_PAID_ONLY ||
      collaborationStatus === CollaborationStatus.RECEIVER_PAID_ONLY);

  const { modalOpen, handleExtend, handleModalDismiss } = usePaymentWindowUiDemo(
    matchId,
    paymentDeadline,
    showDeadline,
  );

  const currentUserSignedNDA = isRequester ? requesterSignedNDA : receiverSignedNDA;
  const otherUserSignedNDA = isRequester ? receiverSignedNDA : requesterSignedNDA;
  const bothSignedNda = requesterSignedNDA && receiverSignedNDA;

  const renderCta = () => {
    if (collaborationStatus === CollaborationStatus.REQUESTED && !isRequester) {
      return (
        <>
          <Button
            variant="filled"
            color="primary"
            size="sm"
            radius="lg"
            leftSection={<IconCheck size={16} />}
            onClick={openAcceptModal}
            loading={isLoading}
          >
            Accept
          </Button>
          <Button
            variant="outline"
            color="gray"
            size="sm"
            radius="lg"
            leftSection={<IconX size={16} />}
            onClick={() => onDecline?.(matchId)}
            loading={isLoading}
          >
            Decline
          </Button>
        </>
      );
    }

    if (collaborationStatus === CollaborationStatus.RECEIVER_DECLINED && isRequester) {
      return (
        <Text size="sm" c="red.6" fw={500}>
          Request Declined
        </Text>
      );
    }

    if (
      (collaborationStatus === CollaborationStatus.BOTH_ACCEPTED ||
        collaborationStatus === CollaborationStatus.REQUESTER_PAID_ONLY ||
        collaborationStatus === CollaborationStatus.RECEIVER_PAID_ONLY) &&
      !userPaid
    ) {
      return (
        <Button
          variant="filled"
          color="primary"
          size="sm"
          radius="lg"
          onClick={() => onMakePayment?.(matchId)}
          loading={isLoading}
        >
          Make Payment
        </Button>
      );
    }

    if (userPaid) {
      if (collaborationStatus !== CollaborationStatus.BOTH_PAID) {
        return null;
      }

      if (!currentUserSignedNDA) {
        return null;
      }

      if (currentUserSignedNDA && !otherUserSignedNDA) {
        return (
          <Tooltip label="Waiting for them to sign NDA" withArrow>
            <Button variant="filled" color="green" size="sm" radius="lg" disabled>
              Open Workspace
            </Button>
          </Tooltip>
        );
      }

      if (bothSignedNda && collaborationStatus !== CollaborationStatus.BOTH_PAID) {
        return (
          <Tooltip label="Waiting for them to make payment" withArrow>
            <Button variant="filled" color="green" size="sm" radius="lg" disabled>
              Open Workspace
            </Button>
          </Tooltip>
        );
      }

      if (bothSignedNda && collaborationStatus === CollaborationStatus.BOTH_PAID) {
        return (
          <Button
            variant="filled"
            color="green"
            size="sm"
            radius="lg"
            onClick={() => onOpenWorkspace?.(matchId)}
            loading={isLoading}
          >
            Open Workspace
          </Button>
        );
      }
    }

    if (collaborationStatus === CollaborationStatus.PAYMENT_EXPIRED) {
      return (
        <Text size="sm" c="orange.6" fw={500}>
          Payment Expired
        </Text>
      );
    }

    return null;
  };

  const viewProfileBtn = (
    <Button
      variant="outline"
      size="sm"
      radius="lg"
      leftSection={<IconEye size={16} />}
      onClick={() => onViewProfile?.(companyId)}
    >
      View Profile
    </Button>
  );

  return (
    <>
      {variant === "table" ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
          <Group gap="sm" wrap="wrap" justify="flex-start">
            {renderCta()}
            {viewProfileBtn}
          </Group>
        </div>
      ) : (
        <Group
          gap="sm"
          wrap="wrap"
          w="100%"
          styles={{
            root: {
              justifyContent: "flex-start",
              "@media (min-width: 48em)": { justifyContent: "flex-end" },
            },
          }}
        >
          {renderCta()}
          {viewProfileBtn}
        </Group>
      )}

      <AcceptInterestModal
        opened={acceptModalOpened}
        onClose={closeAcceptModal}
        onConfirm={() => {
          closeAcceptModal();
          onAccept?.(matchId);
        }}
        isLoading={isLoading}
      />

      <PaymentWindowExtendModal opened={modalOpen} onExtend={handleExtend} onDismiss={handleModalDismiss} />
    </>
  );
}
